import { et3ParseInteger, isPositiveNumber } from "et3-type-guards";
import { useState } from "react";
import { Link } from "react-router-dom";

import i18n from "../../constants/i18n";
import ET3Div from "../../core/et3-div";
import { withLiteObserverAndTheme } from "../../core/et3-enhanced-render";
import Et3Text from "../../core/et3-text";
import getCorePersonalStore from "../../stores/core/core-personal-store";
import { getHeaderIsCompactFromStore } from "./header-layout";
import {
  HEADER_BADGE_BACKGROUND,
  HEADER_COMPACT_ITEM_PADDING_X,
  HEADER_TAB_UNDERLINE_HEIGHT,
  HEADER_COMPACT_LABEL_FONT_SIZE,
  HEADER_BADGE_FONT_SIZE,
  HEADER_BADGE_HEIGHT,
  HEADER_BADGE_PADDING_X,
  HEADER_BADGE_RADIUS,
  HEADER_ITEM_ACTIVE_BACKGROUND,
  HEADER_ITEM_GAP,
  HEADER_ITEM_HEIGHT,
  HEADER_ITEM_HOVER_BACKGROUND,
  HEADER_ITEM_PADDING_X,
  HEADER_ITEM_RADIUS,
  HEADER_LABEL_FONT_SIZE,
  HEADER_TRANSITION
} from "./header-constants";

const KHATTABAT_RESULTS_LABEL = "Results 101M";
const DIGITS_ONLY_PATTERN = /[^0-9]/g;

const HeaderMenuItem = withLiteObserverAndTheme<{
  text: string;
  counter: string;
  link: string;
  enabled: boolean;
  isSelected: boolean;
}>((props) => {
  const { theme, text, counter, link, enabled, isSelected } = props;
  const { colors, unitX, unitY } = theme;
  const [isHovered, setIsHovered] = useState(false);
  const isCompact = getHeaderIsCompactFromStore();
  const itemPaddingX = isCompact
    ? HEADER_COMPACT_ITEM_PADDING_X
    : HEADER_ITEM_PADDING_X;
  const labelFontSize = isCompact
    ? HEADER_COMPACT_LABEL_FONT_SIZE
    : HEADER_LABEL_FONT_SIZE;
  const counterDigits = counter.replace(DIGITS_ONLY_PATTERN, "");
  const counterValue = et3ParseInteger(counterDigits);
  const hasCount = isPositiveNumber(counterValue);
  const isAlarms = hasCount && text === i18n.get("ALARMS");
  const isKhattabatResults = text === KHATTABAT_RESULTS_LABEL;
  const isAiKhattaba = getCorePersonalStore().isKhattabaAiAdmin.get();
  const shouldHideCounter = isKhattabatResults || isAiKhattaba || !hasCount;

  const labelColor = (() => {
    switch (true) {
      case isAlarms:
        return colors.all.red;
      case isSelected:
        return colors.all.bondiBlue;
      case enabled:
        return colors.all.maastrichtBlue;
      default:
        return colors.all.dimGray;
    }
  })();

  const backgroundColor = (() => {
    switch (true) {
      case isSelected:
        return HEADER_ITEM_ACTIVE_BACKGROUND;
      case isHovered && enabled:
        return HEADER_ITEM_HOVER_BACKGROUND;
      default:
        return colors.all.transparent;
    }
  })();

  return (
    <Link
      to={link}
      id="menu-item"
      style={{
        textDecoration: "none",
        boxShadow: "none",
        backgroundColor: colors.all.transparent,
        border: "0px"
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(event) => {
        if (!enabled) event.preventDefault();
      }}
      aria-disabled={!enabled}
    >
      <ET3Div
        isScrollable={false}
        isExtendable={false}
        withShadow={false}
        width={HEADER_ITEM_HEIGHT}
        height={HEADER_ITEM_HEIGHT}
        fDirection="row"
        alignItems="center"
        justifyContent="center"
        color={backgroundColor}
        style={{
          boxSizing: "border-box",
          width: "auto",
          minWidth: 0,
          borderRadius: HEADER_ITEM_RADIUS * unitX,
          borderBottom: `${HEADER_TAB_UNDERLINE_HEIGHT * unitY}px solid ${
            isSelected ? colors.all.bondiBlue : colors.all.transparent
          }`,
          paddingLeft: itemPaddingX * unitX,
          paddingRight: itemPaddingX * unitX,
          gap: HEADER_ITEM_GAP * unitX,
          cursor: enabled ? "pointer" : "default",
          transition: HEADER_TRANSITION,
          whiteSpace: "nowrap"
        }}
        onClick={() => {}}
      >
        <Et3Text
          fontFamily={"MEDIUM"}
          fontSize={labelFontSize}
          text={text}
          isSpan={false}
          color={labelColor}
          style={{
            margin: 0,
            whiteSpace: "nowrap",
            transition: HEADER_TRANSITION
          }}
        />
        {shouldHideCounter ? null : (
          <ET3Div
            isScrollable={false}
            isExtendable={false}
            withShadow={false}
            width={HEADER_BADGE_HEIGHT}
            height={HEADER_BADGE_HEIGHT}
            fDirection="row"
            alignItems="center"
            justifyContent="center"
            color={isAlarms ? colors.all.red : HEADER_BADGE_BACKGROUND}
            style={{
              boxSizing: "border-box",
              width: "auto",
              minWidth: HEADER_BADGE_HEIGHT * unitX,
              height: HEADER_BADGE_HEIGHT * unitY,
              borderRadius: HEADER_BADGE_RADIUS * unitX,
              paddingLeft: HEADER_BADGE_PADDING_X * unitX,
              paddingRight: HEADER_BADGE_PADDING_X * unitX
            }}
            onClick={() => {}}
          >
            <Et3Text
              fontFamily={"BOLD"}
              fontSize={HEADER_BADGE_FONT_SIZE}
              text={counter}
              isSpan={false}
              color={isAlarms ? colors.all.white : colors.all.bondiBlue}
              style={{ margin: 0, whiteSpace: "nowrap" }}
            />
          </ET3Div>
        )}
      </ET3Div>
    </Link>
  );
});

export default HeaderMenuItem;
